import {ParamComponent} from 'panel';

import {ToastType} from 'common/components/toast/types';
import {addToast} from 'common/components/toast-area/toast-manager';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import type {PositionParent} from 'common/components/drop-menu/';
import type {Directions} from 'common/components/drop-menu/types';
import type {IronContextRegistry} from 'common/report/context-registry';

import type {CreateProjectModalAttrs} from '../create-project-modal';
import {template} from './template';
import css from './index.inline.styl';

interface ProjectSelectorProps {
  params?: ProjectSelectorParams;
}

interface ProjectSelectorParams {
  direction?: Directions;
  offset?: number;
  positionParent?: PositionParent;
  searchPlaceholder: string;
  submitCopy: string;
}

interface ProjectSelectorState {
  open: boolean;
  searchFilter: string;
  isCreateProjectModalOpen: boolean;
}

export class ProjectSelector extends ParamComponent<ProjectSelectorParams, ProjectSelectorState, IronContextRegistry> {
  public searchInputEl: HTMLInputElement;
  get config() {
    return {
      css,
      template,
      contexts: [`currentProject` as const, `projects` as const],
      useShadowDom: true,
      defaultState: {
        open: false,
        searchFilter: null,
        isCreateProjectModalOpen: false,
      },
      helpers: {
        handleSearchInputInsertHook: (vnode) => {
          this.searchInputEl = vnode.elm;
        },
        handleMenuChange: (ev) => {
          ev.stopPropagation();
          this.dispatchEvent(new CustomEvent(`menuchange`, {detail: ev.detail}));

          if (ev.detail.state !== `closed`) {
            // menu opening
            this.focusSearch();
          } else {
            // TODO menu closing
          }
        },
        handleSearchInput: (ev) => {
          this.update({searchFilter: ev.target.value});
        },
        handleProjectSelected: (ev): void => {
          this.dispatchEvent(
            new CustomEvent(`select`, {
              detail: {
                targetProjectId: ev.detail.projectId || undefined,
              },
              cancelable: true,
            }),
          );
        },
        handleOpenProjectModal: () => {
          this.update({isCreateProjectModalOpen: true});
        },
        getCreateProjectModalAttrs: (): Partial<CreateProjectModalAttrs> => {
          const currentProject = this.getContext(`currentProject`).get().value;
          return {open: true, 'organization-id': currentProject.organizationId, 'project-id': currentProject.id};
        },
        handleCreateProjectModal: (ev) => {
          try {
            // comes from CreateProjectModalEl
            if (ev?.detail?.error) {
              throw new Error(ev.detail.error);
            }
            const newProjectId = ev?.detail?.response?.id;
            window.location.href = `/report/${newProjectId}/`;
          } catch (error) {
            addToast({type: ToastType.ERROR, message: `Failed to create new project. Error: ${error.message}`});
          }
        },
        handleCancelProjectModal: () => {
          this.update({isCreateProjectModalOpen: false});
        },
      },
      params: {
        direction: String,
        offset: Number,
        positionParent: String,
        searchPlaceholder: String,
        submitCopy: String,
      },
    };
  }

  focusSearch() {
    window.requestAnimationFrame(() => this.searchInputEl.focus());
  }
}

export const ProjectSelectorEl = defineAndMakeJsxFactory<ProjectSelectorProps>(`mp-project-selector`, ProjectSelector);
export const ProjectSelectorReact = panel2react<ProjectSelectorProps>(`mp-project-selector`, ProjectSelector);
